import { BACKEND_URL } from './config'
import { useLivekitStore } from '../stores/livekitStore'
import { useAuthStore } from '../stores/authStore'
import { getSharedAudioContext } from '../components/voice/audioContext'
import { emitVoiceActivity } from './voiceActivity'
import { useVoiceStore } from '../stores/voiceStore'

// Resolve a server-relative /uploads/... URL into something the browser
// can fetch from anywhere — Electron points BACKEND_URL at prod, the
// web app uses same-origin so the prefix is empty and the path stays
// relative. Same trick the rest of the app uses for avatars/photos.
function resolveUrl(fileUrl: string): string {
  if (/^https?:/i.test(fileUrl)) return fileUrl
  return `${BACKEND_URL}${fileUrl}`
}

// Decoded AudioBuffer cache. Soundboard clips are tiny (~50-300 KB) and
// users hammer the same buttons repeatedly, so decoding once and reusing
// the buffer is a dramatic latency win on every replay. Keyed by the
// resolved absolute URL.
const decodedBufferCache = new Map<string, AudioBuffer>()

// Local playback only — no participant.publishData. Used both for the
// sender-side immediate playback and for receivers handling the
// data-channel event.
//
// Why Web Audio (BufferSource) instead of `new Audio(url).play()`:
//   - The receiver path runs inside RoomEvent.DataReceived, which
//     Chrome treats as a NETWORK callback, not a user activation. Even
//     though the GET for the mp3 succeeds (you can see 304 / 200 in
//     the network tab), `<audio>.play()` is silently suppressed by the
//     autoplay policy in this context — and worse, the rejection
//     promise sometimes resolves anyway, so .catch() never fires and
//     there is no console warning to debug from.
//   - The shared AudioContext is unlocked once on voice-channel join
//     (the click that joined IS a user gesture), and stays unlocked
//     for the rest of the session. Any BufferSource scheduled on it
//     plays unconditionally — same path peer microphone audio uses.
//   - Decoded AudioBuffer is cached per URL so repeat-clicks on the
//     same clip skip fetch + decode and just spin up a fresh
//     BufferSource (BufferSources are one-shot — start() can only
//     ever be called once per node).
export async function playSoundLocal(fileUrl: string) {
  const isDeafened = useAuthStore.getState().user?.settings?.isDeafened ?? false
  if (isDeafened) {
    console.warn('[Soundboard] skipping playback — user is deafened', { fileUrl })
    return
  }
  const url = resolveUrl(fileUrl)
  try {
    const ctx = getSharedAudioContext()
    // Resume defensively. The context is normally already running by
    // the time the user could trigger a soundboard clip, but if a
    // browser HMR cycle or a Safari quirk left it suspended, resuming
    // is cheap and idempotent.
    if (ctx.state === 'suspended') {
      await ctx.resume().catch(() => { /* will retry on next play */ })
    }

    let buffer = decodedBufferCache.get(url)
    if (!buffer) {
      const res = await fetch(url, { credentials: 'include' })
      if (!res.ok) {
        console.warn('[Soundboard] fetch failed', { url, status: res.status })
        return
      }
      const arr = await res.arrayBuffer()
      buffer = await ctx.decodeAudioData(arr)
      decodedBufferCache.set(url, buffer)
    }

    const source = ctx.createBufferSource()
    source.buffer = buffer
    // Per-clip gain stage — keeps soundboard volume independent of
    // peerVolume settings on the same context. 0.85 matches the prior
    // HTMLAudioElement default; tweakable if it gets too hot.
    const gain = ctx.createGain()
    gain.gain.value = 0.85
    source.connect(gain)
    gain.connect(ctx.destination)
    source.start(0)
    source.onended = () => {
      try { source.disconnect() } catch { /* noop */ }
      try { gain.disconnect() } catch { /* noop */ }
    }
    console.debug('[Soundboard] play started via Web Audio', { url })
  } catch (err) {
    console.warn('[Soundboard] play failed', { url, err })
  }
}

// Sender path: broadcast over LiveKit's reliable data channel so every
// participant in the room plays the same clip. Falls back to local-only
// when not connected (e.g. user clicked the soundboard while not in a
// voice room — should not normally happen since the menu is gated).
export function playSoundBroadcast(fileUrl: string, name: string) {
  const room = useLivekitStore.getState().room
  playSoundLocal(fileUrl)
  emitVoiceActivity(useVoiceStore.getState().currentChannelId, 'soundboard', { name })
  if (!room) return
  try {
    const payload = new TextEncoder().encode(
      JSON.stringify({ type: 'sound-play', fileUrl, name })
    )
    room.localParticipant.publishData(payload, { reliable: true })
  } catch (err) {
    console.error('Failed to broadcast sound:', err)
  }
}
